/**
 * Backorder cart check
 * Fetches existing backorder quantities for cart items with a future ETA and
 * renders per-item amber badges and a summary banner.
 */

(function () {
  if (window.__backorderCartCheck) return;
  window.__backorderCartCheck = true;

  const ENDPOINT = window.theme?.routes?.preorderQuantities || "/apps/isalbi-periscope/preorder-quantities";
  const CACHE_TTL_MS = 60_000;
  const WARNING_ATTR = "data-backorder-warning";
  const LOADING_ATTR = "data-backorder-loading";
  const BANNER_ATTR = "data-backorder-banner";

  let _cache = null;
  let _cacheTime = 0;

  function getToday() {
    return new Date().toISOString().split("T")[0];
  }

  function fmtPreorderEta(isoDate) {
    const parts = isoDate.split("-").map(Number);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    return `${parts[2]} ${months[parts[1] - 1]} ${parts[0]}`;
  }

  function getEligibleRows() {
    const today = getToday();
    return Array.from(
      document.querySelectorAll(".cart-item[data-product-id]"),
    ).filter((row) => {
      const eta = row.getAttribute("data-variant-eta");
      return eta && eta >= today;
    });
  }

  function buildProducts(rows) {
    return rows
      .map((row) => {
        const id = row.getAttribute("data-product-id") || "";
        const productId = id.startsWith("gid://")
          ? id
          : `gid://shopify/Product/${id}`;
        const sku = row.getAttribute("data-sku") || "";
        return { productId, sku };
      })
      .filter((p) => p.sku);
  }

  async function fetchQuantities(companyLocationId, products) {
    const cacheKey =
      companyLocationId +
      ":" +
      products
        .map((p) => p.sku)
        .sort()
        .join(",");

    if (
      _cache &&
      _cache.key === cacheKey &&
      Date.now() - _cacheTime < CACHE_TTL_MS
    ) {
      return _cache.data;
    }

    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ companyLocationId, products }),
    });
    if (!res.ok) return [];
    const data = await res.json();

    _cache = { key: cacheKey, data };
    _cacheTime = Date.now();

    return data;
  }

  function setRowWarning(row, cumulativeQty) {
    let el = row.querySelector(`[${WARNING_ATTR}]`);
    if (cumulativeQty <= 0) {
      if (el) el.remove();
      return;
    }

    if (!el) {
      el = document.createElement("div");
      el.setAttribute(WARNING_ATTR, "");
      el.className = "backorder-warning text-sm mt-1";
      el.style.cssText =
        "color:#b45309;background:#fef3c7;border-radius:4px;padding:4px 8px;";
      const details = row.querySelector(".cart-item__details");
      if (details) details.appendChild(el);
      else row.appendChild(el);
    }
    const eta = row.getAttribute("data-variant-eta");
    const etaStr = eta ? ` (ETA ${fmtPreorderEta(eta)})` : "";
    const unit = cumulativeQty === 1 ? "unit" : "units";
    el.textContent = `Already on backorder: ${cumulativeQty} ${unit}${etaStr}`;
  }

  function setLoadingState(rows, loading) {
    rows.forEach((row) => {
      let el = row.querySelector(`[${LOADING_ATTR}]`);
      if (loading && !el) {
        el = document.createElement("span");
        el.setAttribute(LOADING_ATTR, "");
        el.style.cssText =
          "font-size:11px;color:#6b7280;display:block;margin-top:2px;";
        el.textContent = "Checking backorders\u2026";
        const details = row.querySelector(".cart-item__details");
        if (details) details.appendChild(el);
      } else if (!loading && el) {
        el.remove();
      }
    });
  }

  function updateBanner(count) {
    let banner = document.querySelector(`[${BANNER_ATTR}]`);
    if (!banner) {
      banner = document.createElement("div");
      banner.setAttribute(BANNER_ATTR, "");
      banner.style.cssText =
        "background:#fef3c7;border:1px solid #f0c040;border-radius:6px;" +
        "padding:10px 14px;margin-bottom:12px;font-size:14px;color:#6b4f00;" +
        "display:flex;gap:8px;align-items:flex-start;";
      banner.hidden = true;
      // Insert before the table/list that wraps cart items
      const firstItem = document.querySelector(".cart-item");
      if (firstItem) {
        const container = firstItem.closest("table, ul, [data-cart-items]");
        if (container && container.parentNode) {
          container.parentNode.insertBefore(banner, container);
        }
      }
    }
    if (count > 0) {
      const line =
        count === 1 ? "line contains an item" : "lines contain items";
      banner.textContent = `\u26a0 ${count} ${line} already on backorder for this location. Review quantities before placing the order.`;
      banner.hidden = false;
    } else {
      banner.hidden = true;
    }
  }

  async function checkBackorders() {
    const session = window.__ISALBI_CONNECT_SESSION__;
    if (!session?.companyLocationGid) return;
    const rawLocationId = session.companyLocationGid;
    const locationGid = String(rawLocationId).startsWith('gid://')
      ? String(rawLocationId)
      : `gid://shopify/CompanyLocation/${rawLocationId}`;

    const rows = getEligibleRows();
    if (rows.length === 0) {
      updateBanner(0);
      return;
    }

    const products = buildProducts(rows);
    if (products.length === 0) {
      updateBanner(0);
      return;
    }

    setLoadingState(rows, true);

    try {
      const results = await fetchQuantities(
        locationGid,
        products,
      );
      const byProduct = new Map(
        results.map((r) => [r.productId, r.cumulativeQuantity]),
      );

      let warningCount = 0;
      rows.forEach((row) => {
        const id = row.getAttribute("data-product-id") || "";
        const gid = id.startsWith("gid://")
          ? id
          : `gid://shopify/Product/${id}`;
        const qty = byProduct.get(gid) ?? 0;
        if (qty > 0) warningCount++;
        setRowWarning(row, qty);
      });

      updateBanner(warningCount);
    } catch {
      // Advisory - silently ignore, clear any stale warnings
      rows.forEach((row) => setRowWarning(row, 0));
      updateBanner(0);
    } finally {
      setLoadingState(rows, false);
    }
  }

  let _running = false;

  function init() {
    checkBackorders();
    document.addEventListener("cart:updated", checkBackorders);
    document.addEventListener("cart:refresh", checkBackorders);

    const cartRoot = document.querySelector('form[action="/cart"], cart-drawer-items, cart-items');
    if (cartRoot) {
      new MutationObserver(() => {
        if (_running) return;
        _running = true;
        checkBackorders().finally(() => {
          _running = false;
        });
      }).observe(cartRoot, { childList: true, subtree: true });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
